import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreateTicket.module.css';

const CreateTicket = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    urgency: 'medium',
    category: '',
    source: 'console',
  });
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [createdTicket, setCreatedTicket] = useState(null);   // { id, displayId, calendlyUrl }
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = React.useRef(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/categories/`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCategories(Array.isArray(data) ? data : data.categories || data.results || []))
      .catch(() => {});
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title cannot exceed 255 characters';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Issue description is required';
    }
    
    if (!formData.urgency) {
      newErrors.urgency = 'Urgency is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const combined = [...attachments, ...newFiles].slice(0, 1); // max 1
    setAttachments(combined);
    // reset the input so same file can be re-added after removal
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('description', formData.description);
      payload.append('urgency', formData.urgency);
      payload.append('category', formData.category);
      payload.append('source', formData.source);
      attachments.forEach(f => payload.append('attachments', f));

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: payload,
      });

      if (!response.ok) throw new Error('Failed to create ticket');

      const data = await response.json();
      setCreatedTicket({ id: data.ticket_id, displayId: data.display_id, calendlyUrl: data.calendly_url || null });
    } catch (error) {
      console.error('Error creating ticket:', error);
      setErrors({ submit: 'Failed to create ticket. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/suspire-helpdesk');
  };

  return (
    <div className={styles.container}>

      {/* ── Success screen (replaces form after ticket created) ─────────────── */}
      {createdTicket ? (
        <div className={styles.formContainer}>
          <div className={styles.successScreen}>
            <div className={styles.successCheckCircle}>✓</div>
            <h2 className={styles.successHeading}>Ticket #{createdTicket.displayId} Created!</h2>
            <p className={styles.successSub}>Your ticket has been submitted. We’ll get back to you soon.</p>



            <button
              className={styles.submitButton}
              onClick={() => navigate(`/suspire-helpdesk/tickets/${createdTicket.id}`)}
            >
              View Ticket →
            </button>
          </div>
        </div>
      ) : (
        <>
      {/* Book-a-call confirmation popup */}

      <div className={styles.formContainer}>
        <h1 className={styles.title}>Create Support Ticket</h1>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="category" className={styles.label}>
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className={`${styles.select} ${errors.category ? styles.selectError : ''}`}
              disabled={isLoading}
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id} title={cat.description || ''}>{cat.name}</option>
              ))}
            </select>
            {errors.category && <div className={styles.errorMessage}>{errors.category}</div>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="title" className={styles.label}>
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief description of your issue"
              className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
              maxLength={255}
              disabled={isLoading}
            />
            {errors.title && <div className={styles.errorMessage}>{errors.title}</div>}
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.label}>
              Issue Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, or screenshots if applicable."
              className={`${styles.textarea} ${errors.description ? styles.textareaError : ''}`}
              rows={6}
              disabled={isLoading}
            />
            {errors.description && <div className={styles.errorMessage}>{errors.description}</div>}
          </div>
          
          {/* File Attachments */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Attachment <span className={styles.optionalTag}>(optional, 1 file max, 10 MB)</span></label>
            <div
              className={styles.dropZone}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const dropped = Array.from(e.dataTransfer.files);
                const combined = [...attachments, ...dropped].slice(0, 1);
                setAttachments(combined);
              }}
            >
              <span className={styles.dropIcon}>📎</span>
              <span>Click or drag files here</span>
              <span className={styles.dropHint}>PNG, JPG, PDF, XLSX, DOCX</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".png,.jpg,.jpeg,.pdf,.xlsx,.docx"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={isLoading}
            />
            {attachments.length > 0 && (
              <ul className={styles.fileList}>
                {attachments.map((f, i) => (
                  <li key={i} className={styles.fileItem}>
                    <span className={styles.fileIcon}>📄</span>
                    <span className={styles.fileName}>{f.name}</span>
                    <span className={styles.fileSize}>({(f.size / 1024).toFixed(1)} KB)</span>
                    <button
                      type="button"
                      className={styles.removeFileBtn}
                      onClick={() => removeAttachment(i)}
                      disabled={isLoading}
                    >✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="urgency" className={styles.label}>
              Urgency *
            </label>
            <select
              id="urgency"
              name="urgency"
              value={formData.urgency}
              onChange={handleInputChange}
              className={`${styles.select} ${errors.urgency ? styles.selectError : ''}`}
              disabled={isLoading}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            {errors.urgency && <div className={styles.errorMessage}>{errors.urgency}</div>}
          </div>
          
          {errors.submit && <div className={styles.errorMessage}>{errors.submit}</div>}
          
          <div className={styles.buttonGroup}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className={styles.spinner}></span>
                  Creating...
                </>
              ) : (
                'Submit Ticket'
              )}
            </button>
            
            <button
              type="button"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
        </>
      )}
    </div>
  );
};

export default CreateTicket;
